import { useState, useEffect } from 'react';
import { Shield, BarChart3, Users, Settings, FileText, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuthStore } from '@/stores/authStore';
import { useNavigate } from 'react-router-dom';
import { ThemeToggle } from '@/components/ThemeToggle';
import { StatsCards } from '@/components/admin/StatsCards';
import { StatsCharts } from '@/components/admin/StatsCharts';
import { UserManagement } from '@/components/admin/UserManagement';
import { InviteManagement } from '@/components/admin/InviteManagement';
import { PromptManagement } from '@/components/admin/PromptManagement';

export default function AdminPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // 检查权限
  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // 加载统计数据
  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/stats');
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="relative min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-2 md:gap-3">
            <Shield className="h-5 w-5 md:h-6 md:w-6" />
            <h1 className="text-lg md:text-xl font-bold">管理员面板</h1>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <ThemeToggle />
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">返回首页</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto p-4 md:p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">概览</span>
            </TabsTrigger>
            <TabsTrigger value="prompts" className="gap-2">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Prompt</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">用户</span>
            </TabsTrigger>
            <TabsTrigger value="invites" className="gap-2">
              <Shield className="h-4 w-4" />
              <span className="hidden sm:inline">邀请码</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2">
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">设置</span>
            </TabsTrigger>
          </TabsList>

          {/* 概览 */}
          <TabsContent value="overview" className="space-y-6">
            {loading ? (
              <div className="text-center py-12">加载中...</div>
            ) : stats ? (
              <>
                <StatsCards stats={stats} />
                <StatsCharts stats={stats} />
              </>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                无法加载统计数据
              </div>
            )}
          </TabsContent>

          {/* Prompt 管理 */}
          <TabsContent value="prompts">
            <PromptManagement />
          </TabsContent>

          {/* 用户管理 */}
          <TabsContent value="users">
            <UserManagement />
          </TabsContent>

          {/* 邀请码管理 */}
          <TabsContent value="invites">
            <InviteManagement />
          </TabsContent>

          {/* 设置 */}
          <TabsContent value="settings">
            <div className="text-center py-12 text-muted-foreground">
              设置功能开发中...
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

